import { useMutation } from '@tanstack/react-query';
import { Col, DatePicker, Form, Row } from 'antd';
import { useForm } from 'antd/es/form/Form';
import { DefaultOptionType } from 'antd/es/select';
import { PlantApi } from 'apis/PlantApis';
import { SaleTeamApi } from 'apis/SaleTeamApi';
import { FormFilter, SelectForm } from 'components';
import RangePickerForm from 'components/RangePicker/RangePickerForm';
import LazySelectForm, {
  ILazySelectParams,
  initLazySelectData,
} from 'components/Select/LazySelectForm';
import { useUser } from 'contexts/UserProvider';
import dayjs from 'dayjs';
import { IPlant } from 'interfaces/Plant/Plant';
import { IFilterTeamSaleCb } from 'interfaces/TeamSale/TeamSale';
import Content from 'layouts/Content';
import { useCallback, useEffect, useState } from 'react';

const spanCol = 8;

interface IFilterHOFollowCustomer {
  isOpen: boolean;
  defaultFilter: any;
  plantData: IPlant[];
  onCancel: () => void;
  onFilter: (values: any) => void;
}

const FilterCNFollowPlan = ({
  isOpen,
  plantData,
  defaultFilter,
  onFilter,
  onCancel,
}: IFilterHOFollowCustomer) => {
  const [form] = useForm();
  const { user } = useUser();
  const [optionFilter] = useState<DefaultOptionType[]>([]);
  const [typeRevenue, setTypeRevenue] = useState([{}]);
  const [dataPlants, setDataPlants] = useState<IPlant[]>([]);
  const [teamData, setTeamData] = useState(initLazySelectData);
  const OPTIONS_PLANT: DefaultOptionType[] = [...dataPlants].map((plant) => {
    return {
      value: plant.id,
      label: plant.id === '' ? plant.name : plant.id,
    };
  });
  const [minMonth, setMinMonth] = useState(dayjs().month(0));
  const [maxMonth, setMaxMonth] = useState(dayjs());

  const { mutate: getPlants } = useMutation({
    mutationFn: () => PlantApi.getAllPlant(),
    onSuccess: (res) => {
      if (res?.data?.getPlant) {
        setDataPlants(res?.data?.getPlant?.items);
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const { mutate: getTypeRevenue } = useMutation({
    mutationFn: () => PlantApi.getTypeRevenueComboBox(),
    onSuccess: (res: any) => {
      setTypeRevenue(
        res.data.getCode.items
          ? res.data.getCode.items.map((item: any) => ({
              value: item.id,
              label: item.name,
            }))
          : [],
      );
    },
    onError: (error) => {
      return console.log(error);
    },
  });

  const { isPending: isGetTeamSaleCbb, mutate: getTeamSaleCbb } = useMutation({
    mutationFn: (params: IFilterTeamSaleCb) =>
      SaleTeamApi.getAllTeamSaleCb(params),
    onSuccess: (res) => {
      if (res?.data?.getTeamSales) {
        const options = (res?.data?.getTeamSales.items ?? []).map(
          (item: any) => ({
            value: `${item?.id}.${item?.subId}.${item?.plantId}`,
            label: `${item?.subId} - ${item?.name}`,
            key: item?.id,
          }),
        );

        setTeamData({
          options,
          totalItems: res?.data?.getTeamSales.pageInfo.totalItems ?? 0,
        });
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });

  const handleYearChange = useCallback(
    (value: any) => {
      const currentYear = dayjs().year();
      const selectedYear = value.year();

      setMinMonth(dayjs().year(selectedYear).month(0));
      setMaxMonth(
        selectedYear === currentYear
          ? dayjs()
          : dayjs().year(selectedYear).month(11),
      );

      form.setFieldsValue({
        year: value,
        month: [
          dayjs().year(selectedYear).month(0),
          selectedYear === currentYear
            ? dayjs()
            : dayjs().year(selectedYear).month(11),
        ],
      });
    },
    [form],
  );

  const setDefaultFilter = useCallback(() => {
    form.setFieldsValue({
      year: dayjs(new Date().getFullYear().toString(), 'YYYY'),
      month: [dayjs().month(0), dayjs()],
    });
  }, [form]);

  const handleCancel = () => {
    form.resetFields();
    setDefaultFilter();
    onCancel();
  };

  const handleTeamSelectScrollChange = useCallback(
    (params: ILazySelectParams) => {
      const { plantId } = form.getFieldsValue();
      const { pageSize, value } = params;

      getTeamSaleCbb({
        plantId: plantId,
        pageSize: pageSize,
        value,
        ...(defaultFilter?.plantId && { plantId: defaultFilter.plantId }),
        ...(user && user?.isQLD && { managerId: user.id }),
        ...(user && user?.isSale && { userId: user.id }),
      });
    },
    [defaultFilter.plantId, form, getTeamSaleCbb, user],
  );

  useEffect(() => {
    form.setFieldsValue({
      ...defaultFilter,
    });
  }, [defaultFilter, form]);

  useEffect(() => {
    getTypeRevenue();
  }, [getTypeRevenue]);

  useEffect(() => {
    getPlants();
  }, [getPlants]);

  useEffect(() => {
    setDefaultFilter();
  }, [setDefaultFilter]);

  return (
    <Content isOpen={isOpen}>
      <FormFilter form={form} onFinish={onFilter} onCancel={handleCancel}>
        <Row gutter={{ xs: 8, sm: 16, md: 24, lg: 32 }}>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Chi nhánh" name="plantId">
              <SelectForm
                placeholder="Chọn chi nhánh"
                options={OPTIONS_PLANT}
                disabled={!(user?.isAdmin || user?.isHO)}
                allowClear={false}
                filterSort={() => 1}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Đội bán hàng" name="teamSalesId">
              <LazySelectForm
                data={teamData}
                loading={isGetTeamSaleCbb}
                placeholder="Chọn đội"
                onSelectScrollChange={handleTeamSelectScrollChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Loại doanh thu" name="typeRevenue">
              <SelectForm
                options={typeRevenue}
                placeholder="Chọn loại doanh thu"
                disabled={
                  plantData.length === 1 || !(user?.isAdmin || user?.isHO)
                }
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Năm" name="year">
              <DatePicker
                allowClear={false}
                name="year"
                picker="year"
                placeholder="Chọn năm"
                className="h-[40px] w-full"
                onChange={handleYearChange}
              />
            </Form.Item>
          </Col>
          <Col span={spanCol} className="h-[80px]">
            <Form.Item label="Từ tháng" name="month">
              <RangePickerForm
                allowClear={false}
                picker="month"
                format="MM/YYYY"
                placeholder={['Chọn tháng', 'Chọn tháng']}
                maxDate={maxMonth}
                minDate={minMonth}
              />
            </Form.Item>
          </Col>
        </Row>
      </FormFilter>
    </Content>
  );
};

export default FilterCNFollowPlan;
